'use client';

import type { ChatStatus } from '@heroui-pro/react/prompt-input';
import { cn, toast } from '@heroui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { handleTRPCError } from '@/utils/error-handler';
import { useAppSelector } from '@/stores/hooks';
import { AiChatComposer, type AiChatComposerSubmitPayload } from './components/AiChatComposer';
import { AiChatHeader } from './components/AiChatHeader';
import { AiChatHistorySheet } from './components/AiChatHistorySheet';
import { AiChatMessageArea } from './components/AiChatMessageArea';
import { streamChatCompletion } from './hooks/use-chat-stream';
import type {
  AiChatPanelMode,
  AiChatPanelProps,
  AiChatUiMessage,
  AiGenerationAssetSummary,
  AiChatSessionSummary,
} from './types';

function createLocalId(prefix: string): string {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? Date.now()}`;
}

export function AiChatPanel({
  className,
  channel = 'ai-chat',
  personaOwnerUserId,
  placeholder,
}: AiChatPanelProps) {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const isHydrated = useAppSelector((s) => s.auth.isHydrated);
  const utils = trpc.useUtils();

  const [mode, setMode] = useState<AiChatPanelMode>('CHAT');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AiChatUiMessage[]>([]);
  const [composerStatus, setComposerStatus] = useState<ChatStatus>('ready');
  const abortRef = useRef<AbortController | null>(null);

  const enabled = isHydrated && isAuthenticated;

  const { data: sessionsData, isLoading: sessionsLoading } = trpc.aiChatApp.listMySessions.useQuery(
    { channel, limit: 80 },
    { enabled }
  );

  const { data: sessionMessages, isLoading: messagesLoading } =
    trpc.aiChatApp.listSessionMessages.useQuery(
      { sessionId: sessionId! },
      { enabled: enabled && !!sessionId && mode === 'CHAT' }
    );

  const { data: generationData, isLoading: generationsLoading } = trpc.assets.listMy.useQuery(
    {
      page: 1,
      pageSize: 100,
      type: 'image',
      channel,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    },
    { enabled }
  );

  const createSessionMutation = trpc.aiChatApp.createSession.useMutation();
  const appendMessageMutation = trpc.aiChatApp.appendMessage.useMutation();
  const generateImageMutation = trpc.aiImage.generateAndPersistToAssets.useMutation();

  const sessions = useMemo(
    () => (sessionsData ?? []) as AiChatSessionSummary[],
    [sessionsData]
  );

  const generationAssets = useMemo(() => {
    const items = (generationData?.items ?? []) as AiGenerationAssetSummary[];
    return items.filter((item) => item.prompt?.trim());
  }, [generationData]);

  useEffect(() => {
    if (!sessionId || mode !== 'CHAT') return;
    if (!sessionMessages) return;
    const mapped: AiChatUiMessage[] = sessionMessages.map((row) => {
      const item = row as { id: string; role: string; content: string };
      return {
        id: item.id,
        role: item.role === 'assistant' ? 'assistant' : 'user',
        text: item.content,
        status: 'completed',
      };
    });
    setMessages(mapped);
  }, [sessionId, sessionMessages, mode]);

  const ensureChatSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const created = await createSessionMutation.mutateAsync({
      channel,
      personaOwnerUserId: personaOwnerUserId ?? null,
    });
    const id = (created as { id: string }).id;
    setSessionId(id);
    await utils.aiChatApp.listMySessions.invalidate();
    return id;
  }, [channel, createSessionMutation, personaOwnerUserId, sessionId, utils.aiChatApp.listMySessions]);

  const handleNewSession = useCallback(async () => {
    if (mode !== 'CHAT') {
      setMode('CHAT');
    }
    abortRef.current?.abort();
    setComposerStatus('ready');
    setSessionId(null);
    setMessages([]);
    try {
      const created = await createSessionMutation.mutateAsync({
        channel,
        personaOwnerUserId: personaOwnerUserId ?? null,
      });
      const id = (created as { id: string }).id;
      setSessionId(id);
      await utils.aiChatApp.listMySessions.invalidate();
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : '新建会话失败');
    }
  }, [channel, createSessionMutation, mode, personaOwnerUserId, utils.aiChatApp.listMySessions]);

  const handleSelectSession = useCallback((id: string) => {
    setMode('CHAT');
    setSessionId(id);
  }, []);

  const handleChatSubmit = useCallback(
    async (payload: Extract<AiChatComposerSubmitPayload, { mode: 'CHAT' }>) => {
      const text = payload.text.trim();
      if (!text && !payload.attachments.length) return;

      const imageRefAssetIds = payload.attachments
        .filter((item) => item.assetId && item.mimeType?.startsWith('image/'))
        .map((item) => ({
          assetId: item.assetId!,
          role: 'reference_image' as const,
        }));

      const displayText = text || (imageRefAssetIds.length ? '（图片）' : '');

      const userMessage: AiChatUiMessage = {
        id: createLocalId('user'),
        role: 'user',
        text: displayText,
        attachments: payload.attachments.length ? payload.attachments : undefined,
        status: 'completed',
      };
      const assistantId = createLocalId('assistant');
      setMessages((prev) => [
        ...prev,
        userMessage,
        { id: assistantId, role: 'assistant', text: '', status: 'streaming' },
      ]);
      setComposerStatus('streaming');

      try {
        const activeSessionId = await ensureChatSession();
        await appendMessageMutation.mutateAsync({
          sessionId: activeSessionId,
          role: 'user',
          content: displayText,
          model: payload.modelApiId,
        });

        const history = [
          ...messages
            .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.text.trim())
            .map((m) => ({ role: m.role, content: m.text.trim() })),
          { role: 'user' as const, content: displayText },
        ];

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const assistantText = (await streamChatCompletion({
          messages: history,
          model: payload.modelApiId,
          personaOwnerUserId,
          ...(imageRefAssetIds.length ? { refAssetIds: imageRefAssetIds } : {}),
          signal: controller.signal,
          onChunk: (chunk) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, text: chunk, status: 'streaming' } : m
              )
            );
          },
        })).trim();

        if (!assistantText) {
          throw new Error('模型未返回有效内容');
        }

        await appendMessageMutation.mutateAsync({
          sessionId: activeSessionId,
          role: 'assistant',
          content: assistantText,
          model: payload.modelApiId,
        });

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, text: assistantText, status: 'completed' } : m
          )
        );
        await utils.aiChatApp.listMySessions.invalidate();
      } catch (e) {
        const message = e instanceof Error ? e.message : '对话失败';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, text: message, status: 'failed' } : m
          )
        );
        toast.danger(message);
      } finally {
        setComposerStatus('ready');
      }
    },
    [
      appendMessageMutation,
      ensureChatSession,
      messages,
      personaOwnerUserId,
      utils.aiChatApp.listMySessions,
    ]
  );

  const handleImageSubmit = useCallback(
    async (payload: Extract<AiChatComposerSubmitPayload, { mode: 'IMAGE' }>) => {
      const prompt = payload.text.trim();
      if (!prompt) {
        toast.danger('请输入提示词');
        return;
      }

      const refAssetIds = payload.attachments
        .filter((item) => item.assetId)
        .map((item) => ({
          assetId: item.assetId!,
          role: 'reference_image' as const,
        }));

      const userMessage: AiChatUiMessage = {
        id: createLocalId('user'),
        role: 'user',
        text: prompt,
        attachments: payload.attachments.length ? payload.attachments : undefined,
        status: 'completed',
      };
      const assistantId = createLocalId('assistant');
      setMessages((prev) => [
        ...prev,
        userMessage,
        { id: assistantId, role: 'assistant', text: '正在生成图片…', status: 'streaming' },
      ]);
      setComposerStatus('streaming');

      try {
        const result = await generateImageMutation.mutateAsync({
          model: payload.modelApiId,
          prompt,
          size: payload.size,
          channel,
          description: prompt.slice(0, 200),
          maxImages: payload.imageSettings.count > 1 ? payload.imageSettings.count : undefined,
          ...(refAssetIds.length ? { refAssetIds } : {}),
        });

        const assets = (result.assets ?? []) as Array<{ url?: string | null }>;
        const images = assets
          .map((asset) => asset.url)
          .filter((url): url is string => Boolean(url))
          .map((src) => ({ src, alt: prompt }));

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  text: images.length ? '' : '生成完成，但未返回图片地址',
                  images,
                  status: 'completed',
                }
              : m
          )
        );
        await utils.assets.listMy.invalidate();
      } catch (e) {
        const message = handleTRPCError(e).message;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, text: message, status: 'failed' } : m
          )
        );
        toast.danger(message);
      } finally {
        setComposerStatus('ready');
      }
    },
    [channel, generateImageMutation, utils.assets.listMy]
  );

  const handleComposerSubmit = useCallback(
    async (payload: AiChatComposerSubmitPayload) => {
      if (payload.mode === 'IMAGE') {
        await handleImageSubmit(payload);
      } else {
        await handleChatSubmit(payload);
      }
    },
    [handleChatSubmit, handleImageSubmit]
  );

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setComposerStatus('ready');
  }, []);

  const emptyHint =
    mode === 'IMAGE'
      ? messagesLoading
        ? '加载中…'
        : '描述你想要的画面，开始生图'
      : messagesLoading
        ? '加载中…'
        : '向 AI 发送消息开始对话';

  return (
    <div className={cn('bg-background flex h-full min-h-0 flex-col', className)}>
      <AiChatHeader
        showNewSession={mode === 'CHAT'}
        onNewSession={() => void handleNewSession()}
        onOpenHistory={() => setHistoryOpen(true)}
      />

      <AiChatMessageArea emptyHint={emptyHint} messages={messages} />

      <div className="bg-background shrink-0 px-4 pb-4 pt-3">
        <div className="mx-auto w-full max-w-[714px]">
          <AiChatComposer
            className="w-full"
            disabled={!enabled}
            mode={mode}
            placeholder={placeholder}
            status={composerStatus}
            onModeChange={setMode}
            onStop={handleStop}
            onSubmit={handleComposerSubmit}
          />
        </div>
      </div>

      <AiChatHistorySheet
        activeSessionId={sessionId}
        generationAssets={generationAssets}
        isLoadingGenerations={generationsLoading}
        isLoadingSessions={sessionsLoading}
        open={historyOpen}
        sessions={sessions}
        onOpenChange={setHistoryOpen}
        onSelectSession={handleSelectSession}
      />
    </div>
  );
}
